// src/services/api.js
import axios from 'axios';
import { logger } from '../utils/logger';

// Production builds must use the configured HTTPS API (scripts/check-build-env.js
// stops a build without one). The localhost fallback is for development only.
const configuredURL = process.env.REACT_APP_API_URL;
if (process.env.NODE_ENV === 'production' && !(configuredURL && configuredURL.startsWith('https://'))) {
  throw new Error('REACT_APP_API_URL must be an https:// URL in production builds.');
}
const baseURL = configuredURL || 'http://localhost:5000/api';

// The session is an HttpOnly cookie set by the API (page scripts cannot read
// it). withCredentials sends it; the X-SelfDrive-Client header is required for
// the API to accept the cookie, which stops other sites from using it.
const api = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
  headers: { 'X-SelfDrive-Client': 'web' },
});

// Earlier versions kept the token and profile in localStorage; remove them.
try {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
} catch {
  // storage unavailable (private mode): nothing to clean up
}

// AuthContext registers this to clear the signed-in user when a session ends.
let onSessionEnded = () => {};
export const setSessionEndedHandler = (handler) => { onSessionEnded = handler; };

api.interceptors.response.use(
  (response) => response,
  (error) => {
    logger.error('API request failed', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
    });

    // The session expired or was revoked (sessions last 8-12 hours for staff,
    // kiosks and admins): forget the user and send them to sign in. The
    // start-up session check opts out, since being signed out is normal there.
    if (error.response?.status === 401 && !error.config?.skipSessionEnded) {
      onSessionEnded();
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  socialLogin: (data) => api.post('/auth/social', data), // { provider, idToken } (Google/Apple) or { provider, accessToken } (Facebook)
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  // The signed-in user, from the session cookie (401 when signed out)
  session: () => api.get('/auth/session', { skipSessionEnded: true }),
  // From the welcome email: confirms the address and signs customers in
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  logout: () => api.post('/auth/logout'),
};

// User profile and settings endpoints
export const usersAPI = {
  // Profile
  getProfile: () => api.get('/users/me'),
  updateProfile: (data) => api.patch('/users/me/profile', data), // { name, phone }
  // A password change signs out every other device; the server gives this
  // one a fresh session cookie.
  changePassword: (currentPassword, newPassword) =>
    api.patch('/users/me/password', { currentPassword, newPassword }),

  // Favorites
  getFavorites: () => api.get('/users/me/favorites'),
  updateFavoritesOrder: (order) => api.patch('/users/me/favorites/order', { order }),
  updateThemePreference: (theme) => api.patch('/users/me/theme', { theme }),

  // Documents
  uploadDocument: (type, file) => {
    const formData = new FormData();
    formData.append('document', file);
    return api.post(`/users/me/documents/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getDocument: (type) => api.get(`/users/me/documents/${type}`),
  // The file itself, fetched with the session (never a token in the URL)
  getDocumentFile: (type) => api.get(`/users/me/documents/${type}/file`, { responseType: 'blob' }),
  deleteDocument: (type) => api.delete(`/users/me/documents/${type}`),
  // Delete the signed-in customer's account: { password } or { confirm: 'DELETE' } (social-only accounts)
  deleteAccount: (confirmation) => api.delete('/users/me', { data: confirmation }),
};

export const vehiclesAPI = {
  getAll: (params) => api.get('/vehicles', { params }),
  getOne: (id) => api.get(`/vehicles/${id}`),
  toggleFavorite: (id) => api.post(`/vehicles/${id}/favorite`),
};

export const appointmentsAPI = {
  create: (data) => api.post('/appointments', data),
  getAll: () => api.get('/appointments'),
  getOne: (id) => api.get(`/appointments/${id}`),
  update: (id, data) => api.patch(`/appointments/${id}`, data),
  // Check in with a QR token: the customer's own session, or the kiosk
  // scanning the customer's QR code.
  checkIn: (qrToken) => api.post('/appointments/checkin', { qrToken }),
};

// Backward-compatibility alias (to be removed in future version)
export const bookingsAPI = appointmentsAPI;

// The test drive waiver (public). Bookings send { accepted: true, version }.
export const waiverAPI = {
  getCurrent: () => api.get('/waiver'),
};

export const dealersAPI = {
  getAll: (location) =>
    api.get('/dealers', {
      params: location ? { location } : {},
    }),
  getSlots: (id) => api.get(`/dealers/${id}/slots`),
  search: (params) => api.get('/dealers/search', { params }),
  getDetails: (id) => api.get(`/dealers/${id}/details`),
  getVehicleAvailableSlots: (dealerId, vehicleId, params) =>
    api.get(`/dealers/${dealerId}/vehicles/${vehicleId}/available-slots`, { params }),
};

export const adminAPI = {
  // body: { name, email, password, dealerId }
  createDealerUser: (data) => api.post('/admin/dealers/user', data),
  // Dealerships (every subscription status), create and edit
  listDealers: () => api.get('/admin/dealers'),
  createDealer: (data) => api.post('/admin/dealers', data),
  updateDealer: (id, data) => api.patch(`/admin/dealers/${id}`, data),
  extendTrial: (id, days) => api.post(`/admin/dealers/${id}/extend-trial`, { days }),
};

export const dealerAPI = {
  getAppointments: () => api.get('/dealer/appointments'),
  updateAppointmentStatus: (id, status) =>
    api.patch(`/dealer/appointments/${id}/status`, { status }),
  // Backward-compatibility aliases
  getBookings: () => api.get('/dealer/appointments'),
  updateBookingStatus: (id, status) =>
    api.patch(`/dealer/appointments/${id}/status`, { status }),

  //availability endpoints
  getAvailability: () => api.get('/dealer/availability'),
  addSlot: (data) => api.post('/dealer/availability', data), // { date, time }
  deleteSlot: (slotId) => api.delete(`/dealer/availability/${slotId}`),

  // Customer document verification
  getCustomerDocuments: (appointmentId) =>
    api.get(`/dealer/appointments/${appointmentId}/customer-documents`),
  // The file itself, fetched with the session (never a token in the URL)
  getCustomerDocumentFile: (appointmentId, type) =>
    api.get(`/dealer/appointments/${appointmentId}/customer-documents/${type}/file`, { responseType: 'blob' }),

  // Staff management (dealer_admin only)
  listStaff: () => api.get('/dealer/staff'),
  inviteStaff: (name, email) => api.post('/dealer/staff', { name, email }),
  setStaffActive: (id, active) => api.patch(`/dealer/staff/${id}`, { active }),
  resendStaffInvite: (id) => api.post(`/dealer/staff/${id}/invite`),

  // CRM lead delivery settings (dealer_admin only)
  getCrmConfig: () => api.get('/dealer/crm-config'),
  updateCrmConfig: (data) => api.patch('/dealer/crm-config', data), // { importEmail?, payloadPlacement?, subjectTemplate?, enabled?, testMode? }
};

 // Dealer availability management
export const dealerAvailabilityAPI = {
  list: (params) => api.get('/dealer/availability', { params }), // { from, to, dealerId? }
  add: (payload) => api.post('/dealer/availability', payload),    // { date, time, capacity?, dealerId? }
  remove: (slotId, params) => api.delete(`/dealer/availability/${slotId}`, { params }), // dealerId?
  rulesGet: (params) => api.get('/dealer/availability/rules', { params }),
  rulesSave: (payload) => api.patch('/dealer/availability/rules', payload), // { rules, dealerId? }
  generate: (payload) => api.post('/dealer/availability/generate', payload), // { from, to, useRules, rules?, dealerId? }
  clearRange: (payload) => api.delete('/dealer/availability/clear', { data: payload }), // { from, to, onlyEmpty, dealerId? }
  // Blackout dates management
  getBlackoutDates: (params) => api.get('/dealer/availability/blackout-dates', { params }),
  addBlackoutDate: (payload) => api.post('/dealer/availability/blackout-dates', payload), // { date, reason?, dealerId? }
  removeBlackoutDate: (id, params) => api.delete(`/dealer/availability/blackout-dates/${id}`, { params }),
  // Calendar settings
  getSettings: (params) => api.get('/dealer/availability/settings', { params }),
  updateSettings: (payload) => api.patch('/dealer/availability/settings', payload), // { appointmentDuration, dealerId? }
};

// Dealer appointments (dashboard)
export const dealerAppointmentsAPI = {
  list: (params) => api.get('/dealer/appointments', { params }), // { status, from, to, dealerId? }
  updateStatus: (id, status) =>
    api.patch(`/dealer/appointments/${id}/status`, { status }),
};

// Backward-compatibility alias
export const dealerBookingsAPI = dealerAppointmentsAPI;

// Kiosk endpoints
export const kioskAPI = {
  searchAppointments: (email) => api.get('/kiosk/appointments/search', { params: { email } }),
  getAppointment: (id) => api.get(`/kiosk/appointments/${id}`),
  // Walk-in, step 1: emails the customer a 6-digit code -> { verificationId, message }
  createAppointment: (data) => api.post('/kiosk/appointments', data),
  // Walk-in, step 2: the code from the email creates the appointment
  verifyAppointment: (verificationId, code) => api.post('/kiosk/appointments/verify', { verificationId, code }),
  // Check-in without the QR code: email a code, then verify it
  requestCheckInCode: (email) => api.post('/kiosk/checkin/code', { email }),
  verifyCheckInCode: (verificationId, code) => api.post('/kiosk/checkin/code/verify', { verificationId, code }),
  getSlots: () => api.get('/kiosk/slots'),
  getProfile: () => api.get('/kiosk/profile'),
  // Leave kiosk mode on this tablet (needs the kiosk password)
  exit: (password) => api.post('/kiosk/exit', { password }),
};

// Dealer vehicles management
export const dealerVehiclesAPI = {
  list: (params) => api.get('/dealer/vehicles', { params }), // { search, page, limit, sortBy, sortOrder, dealerId? }
  updateAvailability: (id, available) =>
    api.patch(`/dealer/vehicles/${id}`, { availableForTestDrive: available }),
  uploadCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/dealer/vehicles/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Billing endpoints
export const billingAPI = {
  getStatus: () => api.get('/billing/status'),
  getPlans: () => api.get('/billing/plans'),
  createCheckoutSession: (plan, frequency) =>
    api.post('/billing/create-checkout-session', { plan, frequency }),
  createPortalSession: () => api.post('/billing/create-portal-session'),
};

export default api;
